import { DeleteOutlined, EditOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { Button, Divider } from "antd";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { Select } from "../BaseSelect";
import { useModalStore } from "@/hooks/Modal/hooks";
import AddQuickTimeModal from "./AddQuickTimeModal";
import DraggableList from "@/components/DraggableList";
import classNames from "classnames";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import { deleteQuickTimeSelectConfig, QuickTimeSelectConfigMode } from "@/services/api/companySetting";
import { collectEvent } from "@/services/dataTrack";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";

interface QuickTimeSelectProps {
    value?: string;
    onChange?: (value: string) => void;
}

const QuickTimeSelect: React.FC<QuickTimeSelectProps> = function (props) {
    const { value, onChange } = props;
    const [open, setOpen] = useState(false);
    const [hoverId, setHoverId] = useState<string>(null);
    const modalStore = useModalStore();
    const orderManageStore = useOrderManagementStore();

    function handleSelect(id: string) {
        setOpen(false);
        setHoverId(null);
        onChange(id);

        const config = orderManageStore.storage.quickTimeSelectConfigs.find((it) => it.id === id);

        if (config) {
            collectEvent(DataTrackEvent.QUICK_TIME_SELECT, {
                content: getLabel(config.mode, config.value),
            });
        }
    }

    function handleClear() {
        handleSelect(null);
    }

    const getLabel = (mode: QuickTimeSelectConfigMode, itemValue: number) => {
        let label = "";

        if (mode === QuickTimeSelectConfigMode.HOURS_AGO) {
            label = `${itemValue}小时前`;
        } else if (mode === QuickTimeSelectConfigMode.RECENT_HOURS) {
            label = `最近${itemValue}小时内`;
        } else if (mode === QuickTimeSelectConfigMode.RECENT_DAYS) {
            label = `最近${itemValue}天内`;
        }

        return label;
    };

    return (
        <Select
            className="mr5"
            loading={orderManageStore.storage.quickTimeSelectConfigLoading}
            open={open}
            value={value}
            onOpenChange={setOpen}
            placeholder="快速选择时间"
            popupMatchSelectWidth={false}
            suffixIcon={null}
            onClear={handleClear}
            allowClear
            popupRender={() => (
                <div onMouseLeave={() => setHoverId(null)}>
                    <DraggableList
                        data={orderManageStore.storage.quickTimeSelectConfigs || []}
                        itemKey="id"
                        onDragEnd={orderManageStore.storage.updateQuickTimeSelectConfigSort}
                        renderItem={(item, { ref, opacity }) => {
                            const { mode } = item;

                            const label = getLabel(mode, item.value);

                            return (
                                <div
                                    onMouseEnter={() => setHoverId(item.id)}
                                    onClick={() => handleSelect(item.id)}
                                    style={{
                                        opacity,
                                        display: "flex",
                                        alignItems: "center",
                                        cursor: "pointer",
                                    }}
                                    className={classNames("ant-select-item ant-select-item-option", {
                                        "ant-select-item-option-active": hoverId === item.id,
                                        "ant-select-item-option-selected": value === item.id,
                                    })}
                                >
                                    <div style={{ flex: 1 }}>{label}</div>
                                    <UnorderedListOutlined
                                        ref={ref}
                                        className="ml10"
                                        style={{ cursor: "move", color: "rgb(24, 144, 255)", padding: "4px" }}
                                        onClick={(e) => e.stopPropagation()}
                                    />

                                    <DeleteOutlined
                                        style={{
                                            visibility: item.isDefault ? "hidden" : "visible",
                                            opacity: hoverId === item.id ? 1 : 0,
                                            color: "red",
                                        }}
                                        className="ml3"
                                        onClick={async (e) => {
                                            if (item.isDefault) return;
                                            e.stopPropagation();

                                            await deleteQuickTimeSelectConfig(item.id);

                                            await orderManageStore.storage.loadQuickTimeSelectConfigs();
                                        }}
                                    />
                                </div>
                            );
                        }}
                    />

                    <Divider style={{ margin: "4px 0" }} />
                    <div style={{ padding: "4px 8px" }}>
                        <Button
                            block
                            icon={<EditOutlined />}
                            type="link"
                            size="small"
                            style={{ textAlign: "left" }}
                            onClick={() => {
                                modalStore.openModal(AddQuickTimeModal);
                            }}
                        >
                            新增快速选择时间
                        </Button>
                    </div>
                </div>
            )}
            options={
                /* 这里的 Option 是为了让 Select 有值显示，但下拉被 popupRender 覆盖 */ [
                    ...(orderManageStore.storage.quickTimeSelectConfigs?.map((opt) => ({
                        key: opt.id,
                        value: opt.id,
                        label: getLabel(opt.mode, opt.value),
                        optionLabel: getLabel(opt.mode, opt.value),
                    })) ?? []),
                ]
            }
        />
    );
};

export default observer(QuickTimeSelect);
